import { ReactElement } from "react";
import { IEAFProduction } from "../../../types";
import { TextField } from "@mui/material";
import { toTons } from "../../../system/utils";
import GaugeChart from "../../../components/charts/gaugeChart";
import { IGaugeConfig } from "../../../components/charts/types";
import { getPalette } from "../../../configuration/configuration";

interface IProps {
  productionData: IEAFProduction[];
}

const ProductionHeader = ({ productionData }: IProps): ReactElement => {
  let totalKwhTon = 0;
  let totalKwh = 0;
  let totalTapToTap = 0;
  let minTapToTap = productionData[0].tapToTap / 60;
  let maxTapToTap = productionData[0].tapToTap / 60;

  let totalWeight = 0;
  let minWeight = toTons(productionData[0].weight);
  let maxWeight = toTons(productionData[0].weight);

  let totalYield = 0;
  let minYield = productionData[0].yield * 100;
  let maxYield = productionData[0].yield * 100;

  const total = productionData.length;

  productionData.forEach(heat => {

    totalKwhTon += heat.kwhTon;
    totalKwh += heat.kwh;
    totalWeight += heat.weight;
    totalTapToTap += heat.tapToTap / 60;
    minTapToTap = Math.min(minTapToTap, heat.tapToTap / 60);
    maxTapToTap = Math.max(maxTapToTap, heat.tapToTap / 60);

    minWeight = Math.min(minWeight, toTons(heat.weight));
    maxWeight = Math.max(maxWeight, toTons(heat.weight));

    totalYield += heat.yield * 100;
    minYield = Math.min(minYield, heat.yield * 100);
    maxYield = Math.max(maxYield, heat.yield * 100);
  });

  totalKwhTon = productionData.length > 0 ? totalKwhTon / productionData.length : 0;
  totalKwh = productionData.length > 0 ? totalKwh / productionData.length : 0;


  const gaugeConfig: IGaugeConfig = {
    minimum: minTapToTap,
    maximum: maxTapToTap,
    start: 270,
    length: 180,
    segments: [
      { start: minTapToTap, end: maxTapToTap, color: getPalette().statusColor.empty.background },
    ],
    format: n => n.toFixed(2)
  };

  const gaugeConfigYield: IGaugeConfig = {
    minimum: minYield,
    maximum: maxYield,
    start: 270,
    length: 180,
    segments: [
      { start: minYield, end: maxYield, color: getPalette().statusColor.empty.background },
    ],
    format: n => n.toFixed(2)
  };

  const gaugeConfigWeight: IGaugeConfig = {
    minimum: minWeight,
    maximum: maxWeight,
    start: 270,
    length: 180,
    segments: [
      { start: minWeight, end: maxWeight, color: getPalette().statusColor.empty.background },
    ],
    format: n => n.toFixed(2)
  };

  const keyBase = `${Date.now()}`;

  return (
    <div className="display-flex-row">
      <div className="flex-grow-0 display-flex-row flex-wrap" style={{ height: 100 }}>

        <TextField className="form-control"
          variant="standard"
          key={`${keyBase}-total-heats`}
           slotProps={{ input: { readOnly: true }}}
          label={"Total Heats"}
          defaultValue={total}/>

        <TextField className="form-control"
          variant="standard"
          key={`${keyBase}-total-tap-to-tap`}
           slotProps={{ input: { readOnly: true }}}
          label={"Total Tap to Tap [min]"}
          defaultValue={totalTapToTap.toFixed(2)}/>

        <TextField className="form-control"
          variant="standard"
          key={`${keyBase}-total-weight`}
           slotProps={{ input: { readOnly: true }}}
          label={"Total Weight [Ton]"}
          defaultValue={toTons(totalWeight).toFixed(2)}/>

        <TextField className="form-control"
          variant="standard"
          key={`${keyBase}-average-kwh`}
          slotProps={{ input: { readOnly: true }}}
          label={"Average kwh"}
          defaultValue={totalKwh.toFixed(2)}/>

        <TextField className="form-control"
          variant="standard"
          key={`${keyBase}-average-kwh-ton`}
           slotProps={{ input: { readOnly: true }}}
          label={"Average kwh / Ton"}
          defaultValue={totalKwhTon.toFixed(2)}/>

      </div>

      {productionData.length > 0 &&
        <div className="display-flex-row flex-grow-0 align-content-center" style={{ width: 200 }}>
          <GaugeChart gaugeConfig={gaugeConfig} value={totalTapToTap / productionData.length} keyValue="Tap to Tap [min]"/>
          <GaugeChart gaugeConfig={gaugeConfigYield} value={totalYield / productionData.length} keyValue="Yield [%]"/>
          <GaugeChart gaugeConfig={gaugeConfigWeight} value={toTons(totalWeight) / productionData.length} keyValue="Weight [Ton]"/>
        </div>
      }
    </div>
  );
};

export default ProductionHeader;
